import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { PackageSearch, ShoppingCart, ClipboardList, User, LogOut, Home } from "lucide-react";

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const nav = [
    { to: "/app", icon: Home, key: "discover", exact: true },
    { to: "/app/orders", icon: ClipboardList, key: "orders" },
    { to: "/app/cart", icon: ShoppingCart, key: "cart", badge: count },
    { to: "/app/profile", icon: User, key: "profile" },
  ];
  const isActive = (n) => (n.exact ? pathname === n.to : pathname.startsWith(n.to));

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="glass sticky top-0 z-40 border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/app" className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center"><PackageSearch className="w-5 h-5 text-white" /></div>
            <span className="font-display font-extrabold text-lg hidden sm:block">Drivons<span className="text-brand">Market</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.key}`}
                className={`relative flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${isActive(n) ? "bg-accent text-brand" : "text-muted-foreground hover:bg-muted"}`}>
                <n.icon className="w-4 h-4" />{t(`nav.${n.key}`)}
                {n.badge > 0 && <span className="ms-1 bg-brand text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{n.badge}</span>}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <span className="text-sm text-muted-foreground hidden sm:block">{user?.name}</span>
            <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={() => { logout(); navigate("/"); }}><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6"><Outlet /></main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t flex">
        {nav.map((n) => (
          <Link key={n.to} to={n.to} data-testid={`mnav-${n.key}`}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-[11px] relative ${isActive(n) ? "text-brand" : "text-muted-foreground"}`}>
            <n.icon className="w-5 h-5" />{t(`nav.${n.key}`)}
            {n.badge > 0 && <span className="absolute top-1.5 end-1/4 bg-brand text-white text-[9px] rounded-full w-4 h-4 flex items-center justify-center">{n.badge}</span>}
          </Link>
        ))}
      </nav>
    </div>
  );
}
